import { SheetModal } from './SheetModal';
import { TabItem, TabsContainer } from './TabsContainer';
import PolicyUpload from './PolicyUpload';
import { PolicyInputForm } from './PolicyInputForm';
import { fundingPolicyTypeDefs, isNullOrUndefined } from '@shared';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPolicyExtractor } from '@client/model';
import { Button } from './ui/button';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { InsuranceFormErrors, usePolicyForm } from '../util/usePolicyForm';
import { InfoIcon, Sparkles, TriangleAlert } from 'lucide-react';
import { cn } from '@client/lib';
import { SendPartnerLinkRequestModal } from '@/features/add-partner';

type AddPolicyModalProps = {
  open: boolean;
  onClose: () => void;
  fundingSourceId?: string;
  // When true, show policy in read-only (display)
  readOnly?: boolean;
};

type DialogType = 'unsavedChanges' | 'jointPolicyNoPartner' | 'extractionBlocking' | null;

interface DialogState {
  isOpen: boolean;
  type: DialogType;
  onConfirm: () => void;
  onCancel: () => void;
}

interface DialogProps {
  showWarning: boolean;
  handleCancelClose: () => void;
  handleConfirmClose: () => void;
  dialogTitle?: string;
  dialogDescription?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  destructive?: boolean;
  showIcon?: boolean;
}

export function AddPolicyModal({
  open,
  onClose,
  fundingSourceId,
  readOnly = false,
}: AddPolicyModalProps) {
  const { processedResponse, status } = useSelector(selectPolicyExtractor);
  const [showLinkPartnerDialog, setShowLinkPartnerDialog] = useState(false);

  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    type: null,
    onConfirm: () => {},
    onCancel: () => {},
  });

  const {
    policyState,
    formErrors,
    hasChangedField,
    onSubmit,
    resetState,
    handleChangePolicyField,
    onImportPolicyFieldValues,
    showJointPolicyNoPartnerDialog,
    setShowJointPolicyNoPartnerDialog,
    policyFile,
    setPolicyFile,
    maxBenefitsInterval,
    handleMaxBenefitsIntervalChange,
  } = usePolicyForm(fundingSourceId, onClose);

  const [activeTab, setActiveTab] = useState(fundingSourceId ? 'manualInput' : 'uploadPolicy');

  const closeDialog = useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      isOpen: false,
      type: null,
    }));
  }, [setDialogState]);

  const resetAndClose = useCallback(() => {
    closeDialog();
    onClose();
    resetState();
    setActiveTab('uploadPolicy');
  }, [closeDialog, onClose, resetState, setActiveTab]);

  const handleAddPartnerLinkClick = useCallback(() => {
    closeDialog();
    setShowLinkPartnerDialog(true);
  }, [closeDialog, setShowLinkPartnerDialog]);

  const renderDialog = useCallback(() => {
    if (!dialogState.type) return null;

    const baseProps: DialogProps = {
      showWarning: dialogState.isOpen,
      handleCancelClose: dialogState.onCancel,
      handleConfirmClose: dialogState.onConfirm,
    };

    switch (dialogState.type) {
      case 'unsavedChanges':
        return <UnsavedChangesDialog {...baseProps} />;

      case 'jointPolicyNoPartner':
        return (
          <UnsavedChangesDialog
            {...baseProps}
            dialogTitle="Partner Account Required"
            dialogDescription="In order to upload a joint policy, you must link a partner account. Would you like to link a partner now, or upload a different policy?"
            primaryButtonText="Add Partner Link"
            secondaryButtonText="Upload Different Policy"
            destructive={false}
          />
        );

      case 'extractionBlocking':
        return (
          <UnsavedChangesDialog
            {...baseProps}
            dialogTitle="Hold on, we're still extracting the policy."
            dialogDescription="Please wait until the extraction is complete before closing this window. You'll be able to continue once it's finished."
            primaryButtonText="Understood"
            secondaryButtonText=""
            destructive={false}
            showIcon={false}
          />
        );

      default:
        return null;
    }
  }, [dialogState]);

  const openUnsavedChangesDialog = useCallback(() => {
    setDialogState({
      isOpen: true,
      type: 'unsavedChanges',
      onConfirm: resetAndClose,
      onCancel: closeDialog,
    });
  }, [resetAndClose, closeDialog]);

  const openJointPolicyNoPartnerDialog = useCallback(() => {
    setDialogState({
      isOpen: true,
      type: 'jointPolicyNoPartner',
      onConfirm: handleAddPartnerLinkClick,
      onCancel: () => {
        closeDialog();
        resetState();
        setPolicyFile(null);
        setActiveTab('uploadPolicy');
      },
    });
  }, [handleAddPartnerLinkClick, closeDialog, resetState, setPolicyFile, setActiveTab]);

  const openExtractionBlockingDialog = useCallback(() => {
    setDialogState({
      isOpen: true,
      type: 'extractionBlocking',
      onConfirm: closeDialog,
      onCancel: closeDialog,
    });
  }, [closeDialog]);

  const handleClose = () => {
    if (hasChangedField) {
      openUnsavedChangesDialog();
    } else {
      onClose();
      resetState();
      setActiveTab('uploadPolicy');
    }
  };

  // Ensure the correct tab is selected when the modal is opened
  useEffect(() => {
    if (readOnly && open) {
      setActiveTab('manualInput');
    } else if (fundingSourceId && open) {
      setActiveTab('manualInput');
    } else if (!fundingSourceId && open) {
      setActiveTab('uploadPolicy');
    }
  }, [fundingSourceId, open, readOnly]);

  useEffect(() => {
    if (showJointPolicyNoPartnerDialog) {
      openJointPolicyNoPartnerDialog();
      setShowJointPolicyNoPartnerDialog(false);
    }
  }, [
    showJointPolicyNoPartnerDialog,
    openJointPolicyNoPartnerDialog,
    setShowJointPolicyNoPartnerDialog,
  ]);

  const tabs: TabItem[] = [
    {
      value: 'uploadPolicy',
      label: (
        <span className="flex items-center gap-2">
          <Sparkles
            className="text-primary-dark h-4 w-4"
            strokeWidth={1.5}
          />
          Auto Fill (from PDF)
        </span>
      ),
      content: (
        <PolicyUpload
          resetState={resetState}
          policyFile={policyFile}
          setPolicyFile={setPolicyFile}
        />
      ),
    },
    {
      value: 'manualInput',
      label: 'Manual Input',
      content: (
        <PolicyInputForm
          policyState={policyState}
          handleChangePolicyField={handleChangePolicyField}
          formErrors={formErrors}
          maxBenefitsInterval={maxBenefitsInterval}
          handleMaxBenefitsIntervalChange={handleMaxBenefitsIntervalChange}
          readOnly={readOnly}
        />
      ),
    },
  ];

  const isGroupPolicy =
    policyState.policyType && fundingPolicyTypeDefs[policyState.policyType].isGroupPolicy;

  const isExtracting = status === 'initiated' || status === 'polling';

  const handleBlockedClose = () => {
    if (isExtracting) {
      openExtractionBlockingDialog();
    } else {
      handleClose();
    }
  };

  return (
    <SheetModal
      open={open}
      testId="add-policy-modal"
      contentTestId="add-policy-modal"
      onClose={handleBlockedClose}
      title={readOnly ? 'View Policy' : fundingSourceId ? 'Edit Policy' : 'Add Policy'}
      subTitle={
        readOnly
          ? 'Policy details'
          : fundingSourceId
            ? 'Edit the details of your policy'
            : 'Upload a policy or provide policy details'
      }
      className="md:min-w-162.5 shadow-2xl"
      modal={true}
      primaryButton={
        readOnly
          ? undefined
          : renderModalFooter({
              showSubmitButton:
                activeTab === 'manualInput' &&
                Object.keys(formErrors).length === 0 &&
                !isGroupPolicy,
              showDisclaimerImportButton:
                activeTab === 'uploadPolicy' && !isNullOrUndefined(processedResponse),
              showFormErrors: activeTab === 'manualInput' && Object.keys(formErrors).length > 0,
              formErrors,
              onImportPolicyFieldValues: () => {
                setActiveTab('manualInput');
                onImportPolicyFieldValues();
              },
              onSubmit,
            })
      }
    >
      <div className="py-4">
        <TabsContainer
          tabs={tabs}
          value={activeTab}
          onValueChange={setActiveTab}
          defaultValue="uploadPolicy"
          hideTabs={!!fundingSourceId || readOnly}
          forceMount={true}
        />

        {renderDialog()}

        <SendPartnerLinkRequestModal
          open={showLinkPartnerDialog}
          onClose={() => {
            setShowLinkPartnerDialog(false);
            onClose();
            resetState();
            setActiveTab('uploadPolicy');
          }}
        />
      </div>
    </SheetModal>
  );
}

function renderModalFooter({
  showSubmitButton,
  showDisclaimerImportButton,
  showFormErrors,
  formErrors,
  onImportPolicyFieldValues,
  onSubmit,
}: {
  showSubmitButton: boolean;
  showDisclaimerImportButton: boolean;
  showFormErrors: boolean;
  formErrors: InsuranceFormErrors;
  onImportPolicyFieldValues: () => void;
  onSubmit: () => void;
}) {
  if (showSubmitButton) {
    return (
      <WarningInfoDisclaimer
        type="info"
        bodyText={
          <p>
            By submitting, you acknowledge that Waterlily calculates the projected LTC coverage of
            each policy independently. If multiple policies are added, each is assessed as if it
            were the sole provider of coverage. No sequencing or priority is assumed, meaning one
            policy does not pay first before another covers the remaining costs. As a result, LTC
            coverage could be overestimated if multiple policy's benefits are applied to the same
            projected costs.
          </p>
        }
        buttonText="Submit"
        onClick={onSubmit}
      />
    );
  }
  if (showDisclaimerImportButton) {
    return (
      <WarningInfoDisclaimer
        type="info"
        bodyText={
          <p>
            PLEASE NOTE: The extraction, modeling and calculation service is provided "AS IS," and
            Waterlily does not guarantee accuracy. By using this tool, you acknowledge and agree
            that you, not Waterlily, are responsible for verifying and correcting the extraction and
            calculations. If you submit multiple policies, each policy is assessments as if it is
            the sole coverage and Waterlily does not apply or calculate policy sequencing or
            priority; so, the calculations herein could overestimate your LTC coverage if you have
            multiple policies. Additionally, results may be less reliable when working with
            image-based (scanned) PDF documents. See{' '}
            <a
              className="underline"
              href="https://www.waterlily.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{' '}
            for more details and disclaimers.
          </p>
        }
        buttonText="Acknowledge and Import"
        onClick={onImportPolicyFieldValues}
      />
    );
  }
  if (showFormErrors) {
    return (
      <WarningInfoDisclaimer
        type="warning"
        bodyText={<FormErrorMessage formErrors={formErrors} />}
        buttonText="Submit"
        onClick={onSubmit}
        buttonDisabled={true}
      />
    );
  }
  return null;
}

function WarningInfoDisclaimer({
  type,
  bodyText,
  buttonText,
  onClick,
  buttonDisabled,
}: {
  type: 'warning' | 'info';
  bodyText: string | React.ReactNode;
  buttonText?: string;
  onClick?: () => void;
  buttonDisabled?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-2 rounded-lg p-4',
        type === 'info' && 'bg-blue-50',
        type === 'warning' && 'bg-yellow-50',
      )}
    >
      {type === 'info' && <InfoIcon className="h-5 w-5 text-blue-500" />}
      {type === 'warning' && <TriangleAlert className="h-5 w-5 text-yellow-500" />}
      <div className="text-xs text-gray-700">{bodyText}</div>
      {buttonText && onClick && (
        <Button
          className="mt-2 rounded-full text-lg font-medium"
          onClick={onClick}
          disabled={buttonDisabled}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
}

interface FormErrorMessageProps {
  formErrors: InsuranceFormErrors;
}

function FormErrorMessage({ formErrors }: FormErrorMessageProps) {
  const formErrorsCount = Object.keys(formErrors).length;
  const waitingPeriodAndBenefitsUtilizedErrorExists =
    'waitingPeriodAndBenefitsUtilized' in formErrors;
  const jointPolicyNoPartnerErrorExists = 'jointPolicyNoPartner' in formErrors;

  // Only count non-dialog errors for required field error message
  const requiredFieldErrorExists =
    (waitingPeriodAndBenefitsUtilizedErrorExists && formErrorsCount > 1) ||
    (jointPolicyNoPartnerErrorExists && formErrorsCount > 1) ||
    (!waitingPeriodAndBenefitsUtilizedErrorExists &&
      !jointPolicyNoPartnerErrorExists &&
      formErrorsCount > 0);

  return (
    <ul className="space-y-2 text-sm">
      <span>Oops! Please fix the following issues:</span>
      {requiredFieldErrorExists && (
        <li>
          <span className="font-medium">Required fields:</span> Please fill out all required fields.
        </li>
      )}
      {waitingPeriodAndBenefitsUtilizedErrorExists && (
        <li>
          <span className="font-medium">Invalid input:</span> Remaining waiting period and benefits
          utilized cannot both be greater than 0.
        </li>
      )}
    </ul>
  );
}
